import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { LoginPage } from "./auth/LoginPage";
import { StaffLayout } from "./StaffLayout";
import { QuotesList } from "./quotes/QuotesList";
import { AcceptedQueue } from "./quotes/AcceptedQueue";
import { ProvisionWorkbench } from "./quotes/ProvisionWorkbench";
import { QuoteEditor } from "./quotes/QuoteEditor";
import { BlockBuilder } from "./quotes/builder/BlockBuilder";
import { TemplateBuilder } from "./quotes/builder/TemplateBuilder";
import { TemplatesPage } from "./templates/TemplatesPage";
import { LeadsBoard } from "./leads/LeadsBoard";
import { LeadsList } from "./leads/LeadsList";
import { JobsList } from "./jobs/JobsList";
import { JobDetail } from "./jobs/JobDetail";
import { OpenApplications } from "./jobs/OpenApplications";
import { OrderBook } from "./jobs/OrderBook";
import { OnHoldJobs } from "./jobs/OnHoldJobs";
import { ArchiveWorkbench } from "./jobs/ArchiveWorkbench";
import { ClientsList } from "./clients/ClientsList";
import { ClientDetail } from "./clients/ClientDetail";
import { ClientArchiveWorkbench } from "./clients/ClientArchiveWorkbench";
import { BuildersList } from "./builders/BuildersList";
import { BuilderDetail } from "./builders/BuilderDetail";
import { InvoicesDashboard } from "./invoices/InvoicesDashboard";
import { LettersInbox } from "./letters/LettersInbox";
import { UnreadProvider } from "./letters/UnreadContext";
import { ChatFeedProvider } from "./chat/ChatContext";
import { AcceptedCountProvider } from "./quotes/AcceptedCountContext";
import { PushProvider } from "./push/PushContext";
import { MessagesPage } from "./chat/MessagesPage";
import { DocumentsPage } from "./chat/DocumentsPage";
import { ImportHistoryPage } from "./chat/ImportHistoryPage";
import { ReviewQueue } from "./review/ReviewQueue";
import { ShellJobMerge } from "./review/ShellJobMerge";
import { ReviewLetters } from "./review/ReviewLetters";
import { TasksDashboard } from "./tasks/TasksDashboard";
import { TasksBoardPage } from "./tasks/TasksBoardPage";
import { FeedbackOpenPage } from "./feedback/FeedbackOpenPage";
import { FeedbackReceivedPage } from "./feedback/FeedbackReceivedPage";
import { StudioDashboard } from "./dashboard/StudioDashboard";
import { FullPageSpinner } from "@/components/ui";

/**
 * Staff console entry. Enforces the access gate (spec §3): the staff UI renders
 * only when authenticated AND the role grants console access (admin or staff).
 * Anyone else sees the login.
 *
 * Inside the console both roles are equal, with ONE exception: Review (the
 * duplicate/merge reconciliation queues) is admin-only. Add further admin-only
 * areas by flagging the nav section `adminOnly` and guarding its routes here —
 * do not narrow the console gate itself.
 */
export function StaffApp() {
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  );
}

function Gate() {
  const { hasStaffAccess, isAdmin, loading } = useAuth();

  if (loading) return <FullPageSpinner label="Checking access…" />;
  if (!hasStaffAccess) return <LoginPage />;

  return (
    <UnreadProvider>
      <ChatFeedProvider>
      <AcceptedCountProvider>
      <PushProvider>
      <Routes>
        <Route element={<StaffLayout />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<StudioDashboard />} />
          <Route path="leads" element={<LeadsBoard />} />
          <Route path="leads/list" element={<LeadsList />} />
          <Route path="jobs" element={<JobsList />} />
          {/* Static segments outrank `jobs/:id`, so these match before the
              detail route despite the order. */}
          <Route path="jobs/open" element={<OpenApplications />} />
          <Route path="jobs/order-book" element={<OrderBook />} />
          <Route path="jobs/on-hold" element={<OnHoldJobs />} />
          <Route path="jobs/archive" element={<ArchiveWorkbench />} />
          <Route path="jobs/:id" element={<JobDetail />} />
          {/* Static /staff/clients lists; clients/archive is the workbench;
              the dynamic :id is the client hub (static outranks the param). */}
          <Route path="clients" element={<ClientsList />} />
          <Route path="clients/archive" element={<ClientArchiveWorkbench />} />
          <Route path="clients/:id" element={<ClientDetail />} />
          <Route path="builders" element={<BuildersList />} />
          <Route path="builders/:id" element={<BuilderDetail />} />
          <Route path="quotes" element={<QuotesList />} />
          <Route path="quotes/accepted" element={<AcceptedQueue />} />
          <Route path="quotes/:quoteId/provision" element={<ProvisionWorkbench />} />
          <Route path="quotes/:quoteId" element={<QuoteEditor />} />
          <Route path="quotes/:quoteId/edit" element={<BlockBuilder />} />
          <Route path="templates" element={<TemplatesPage />} />
          <Route path="templates/:templateId/edit" element={<TemplateBuilder />} />
          <Route path="invoices" element={<InvoicesDashboard />} />
          <Route path="letters" element={<LettersInbox />} />
          {/* Chat — client-portal counterpart: Messages + Documents split views.
              Import History is the Moxo migration utility (right-aligned sub). */}
          <Route path="chat" element={<MessagesPage />} />
          <Route path="chat/documents" element={<DocumentsPage />} />
          <Route path="chat/import-history" element={<ImportHistoryPage />} />
          {/* Review is the console's one admin-only section (navConfig
              `adminOnly`): the merge queues are destructive and hard to unpick.
              Unmounted for staff, so the `*` fallback below redirects them. */}
          {isAdmin ? (
            <>
              <Route path="review" element={<ReviewQueue />} />
              <Route path="review/jobs" element={<ShellJobMerge />} />
              <Route path="review/letters" element={<ReviewLetters />} />
            </>
          ) : null}
          <Route path="tasks" element={<TasksDashboard />} />
          <Route path="tasks/board" element={<TasksBoardPage />} />
          {/* Static segments outrank params; feedback/received matches before
              feedback. */}
          <Route path="feedback" element={<FeedbackOpenPage />} />
          <Route path="feedback/received" element={<FeedbackReceivedPage />} />
          <Route path="*" element={<Navigate to="quotes" replace />} />
        </Route>
      </Routes>
      </PushProvider>
      </AcceptedCountProvider>
      </ChatFeedProvider>
    </UnreadProvider>
  );
}
